import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Send, AtSign, Mail, RefreshCw, Check } from "lucide-react";

const SAMPLE_JD = `We're hiring a Senior Frontend Engineer at Northwind Studios — a YC-backed design tools startup based in San Francisco.

You'll own the React/TypeScript codebase that powers our 80k+ daily users, ship pixel-perfect UI from Figma, and partner closely with our design lead.

Must-haves:
• 5+ years building production React apps
• Deep TypeScript + Tailwind experience
• Comfort with WebGL or canvas-based UIs

Nice-to-have: open-source contributions, design sense, freelance/agency background.

Send your portfolio + a short note to grace@northwindstudios.com.`;

const SAMPLE_SUBJECT = "Senior FE — 6 yrs of React/TS, shipped design tools at scale";

const SAMPLE_BODY = `Hi Grace,

Saw the Northwind FE role and wanted to reach out — I've spent the last 6 years building React/TypeScript apps for design-heavy products, including 2 years on a Figma-plugin marketplace where I owned the entire frontend (~120k MAU).

A few signals that might be relevant:
• Shipped a canvas-based whiteboard rebuild in React that cut interaction latency by 60%
• Contribute to react-flow (300+ stars) and write about WebGL UI patterns
• Comfortable working tightly with design — most of my freelance work is rebuild/polish gigs

I'd love to learn more about the codebase you're building. Portfolio: alex.dev · LinkedIn: /in/alex-mendez

Best,
Alex`;

export default function DemoSection() {
  const [stage, setStage] = useState("idle"); // idle | generating | done
  const [typedSubject, setTypedSubject] = useState("");
  const [typedBody, setTypedBody] = useState("");
  const sectionRef = useRef(null);

  // Auto-trigger when scrolled into view
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && stage === "idle") {
            setTimeout(() => handleGenerate(), 600);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(node);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const handleGenerate = () => {
    setStage("generating");
    setTypedSubject("");
    setTypedBody("");
    // Fake API delay
    setTimeout(() => typewriter(SAMPLE_SUBJECT, setTypedSubject, 18, () => {
      setTimeout(() => typewriter(SAMPLE_BODY, setTypedBody, 8, () => setStage("done")), 200);
    }), 1200);
  };

  const typewriter = (text, setter, speed, done) => {
    let i = 0;
    const tick = () => {
      i += 1;
      setter(text.slice(0, i));
      if (i < text.length) setTimeout(tick, speed);
      else done && done();
    };
    tick();
  };

  return (
    <section ref={sectionRef} id="demo" className="relative max-w-7xl mx-auto px-6 py-20 border-t border-slate-200">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
        <div>
          <span className="label-xs">[03] Live preview</span>
          <h2 className="mt-3 text-3xl md:text-4xl font-display tracking-tight">Watch it write your next pitch.</h2>
        </div>
        <button onClick={handleGenerate} disabled={stage === "generating"}
          className="text-sm text-[#0047FF] hover:text-[#003BE3] inline-flex items-center gap-1.5 font-medium disabled:opacity-50"
          data-testid="demo-replay">
          <RefreshCw className={`h-3.5 w-3.5 ${stage === "generating" ? "animate-spin" : ""}`} />
          {stage === "done" ? "Replay" : "Run demo"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT: Sample JD */}
        <div className="tech-card p-6 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <span className="label-xs">Job description</span>
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-500 font-semibold">Sample</span>
          </div>
          <pre className="flex-1 w-full font-mono text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap min-h-[380px]" data-testid="demo-jd">
            {SAMPLE_JD}
          </pre>
          <button onClick={handleGenerate} disabled={stage === "generating"}
            className="btn-primary mt-4 justify-center" data-testid="demo-generate-btn">
            {stage === "generating" ? (<><RefreshCw className="h-4 w-4 animate-spin" /> Drafting…</>) : (<><Sparkles className="h-4 w-4" /> Draft email</>)}
          </button>
        </div>

        {/* RIGHT: Generated email */}
        <div className="tech-card p-6 flex flex-col relative overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <span className="label-xs">Preview</span>
            {stage !== "idle" && (
              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800">
                <AtSign className="h-3 w-3" /> Detected: grace@northwindstudios.com
              </span>
            )}
          </div>

          {stage === "idle" && (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm min-h-[380px] text-center">
              <div>
                <Mail className="h-8 w-8 mx-auto text-slate-300" />
                <p className="mt-3">Click "Draft email" to see the AI generate a personalized response.</p>
              </div>
            </div>
          )}

          {stage !== "idle" && (
            <div className="space-y-3 flex-1 min-h-[380px]">
              <div>
                <label className="label-xs block mb-1.5">Subject</label>
                <div className="border border-slate-200 bg-white rounded-md px-4 py-2.5 font-medium text-slate-900 min-h-[42px]" data-testid="demo-subject">
                  {typedSubject}
                  {stage === "generating" && typedSubject.length < SAMPLE_SUBJECT.length && (
                    <span className="inline-block w-[2px] h-4 bg-[#0047FF] ml-0.5 animate-pulse align-middle" />
                  )}
                </div>
              </div>
              <div>
                <label className="label-xs block mb-1.5">Body</label>
                <pre className="border border-slate-200 bg-white rounded-md px-4 py-3 text-[13px] leading-relaxed font-sans text-slate-700 whitespace-pre-wrap min-h-[260px]" data-testid="demo-body">
                  {typedBody}
                  {stage === "generating" && typedBody.length < SAMPLE_BODY.length && (
                    <span className="inline-block w-[2px] h-4 bg-[#0047FF] ml-0.5 animate-pulse align-middle" />
                  )}
                </pre>
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap pt-2">
            <div className="text-xs text-slate-500 flex items-center gap-1">
              {stage === "done" ? (
                <><Check className="h-3.5 w-3.5 text-emerald-600" /> Ready to send from <span className="font-mono text-slate-900">your@gmail.com</span></>
              ) : "Sent from your own Gmail — replies come to you."}
            </div>
            <Link
              to="/auth?mode=signup"
              data-testid="demo-send-cta"
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-sm transition-all ${
                stage === "done"
                  ? "bg-[#0047FF] text-white hover:bg-[#003BE3] shadow-sm"
                  : "bg-slate-100 text-slate-400 cursor-not-allowed"
              }`}
              onClick={(e) => { if (stage !== "done") e.preventDefault(); }}
            >
              Send via Gmail <Send className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      <p className="mt-6 text-center text-sm text-slate-500">
        This is a sample preview. <Link to="/auth?mode=signup" className="text-[#0047FF] hover:underline font-medium">Sign up</Link> to generate & send your own.
      </p>
    </section>
  );
}
